/**
 * Utilidades para el sistema de animación de timers
 */

export interface TimerConfig {
  enabled: boolean;
  autoOnEnabled: boolean;
  autoOffEnabled: boolean;
  oneTimeOnly: boolean;
  autoOnHours: number;
  autoOnMinutes: number;
  autoOnSeconds: number;
  autoOffHours: number;
  autoOffMinutes: number;
  autoOffSeconds: number;
}

export type TimerRunMode = 'autoOn' | 'autoOff';

/** Interpreta estado de potencia on/off de telemetría o control. */
export const isPowerOnState = (estado: unknown): boolean => {
  return (
    estado === true ||
    estado === 1 ||
    estado === 'on' ||
    estado === 'ON' ||
    String(estado).toLowerCase() === 'true'
  );
};

const secondsFromParts = (hours: number, minutes: number, seconds: number): number =>
  (hours * 3600) + (minutes * 60) + seconds;

/**
 * Qué timer debería estar corriendo según estado del dispositivo.
 * - Encendido → auto-off (cuenta para apagar)
 * - Apagado → auto-on (cuenta para encender)
 */
export const getRunningTimerMode = (
  timerConfig: TimerConfig | null | undefined,
  deviceIsOn: boolean
): TimerRunMode | null => {
  if (!timerConfig?.enabled) return null;
  if (deviceIsOn && timerConfig.autoOffEnabled) return 'autoOff';
  if (!deviceIsOn && timerConfig.autoOnEnabled) return 'autoOn';
  return null;
};

/** Si la UI debe mostrar anillo/countdown para el estado actual. */
export const shouldShowDeviceTimer = (
  isTimerActive: boolean,
  timerConfig: TimerConfig | null | undefined,
  deviceIsOn: boolean
): boolean => {
  return Boolean(isTimerActive && getRunningTimerMode(timerConfig, deviceIsOn));
};

/**
 * Tiempo total del timer en segundos según el modo que corresponde al estado.
 * Si no se pasa deviceIsOn, usa auto-off si está habilitado, si no auto-on (fallback).
 */
export const getTotalTimerSeconds = (
  timerConfig: TimerConfig,
  deviceIsOn?: boolean
): number => {
  if (!timerConfig.enabled) return 0;

  const mode =
    deviceIsOn === undefined
      ? (timerConfig.autoOffEnabled
          ? 'autoOff'
          : timerConfig.autoOnEnabled
            ? 'autoOn'
            : null)
      : getRunningTimerMode(timerConfig, deviceIsOn);

  if (mode === 'autoOff') {
    return secondsFromParts(
      timerConfig.autoOffHours,
      timerConfig.autoOffMinutes,
      timerConfig.autoOffSeconds
    );
  }
  if (mode === 'autoOn') {
    return secondsFromParts(
      timerConfig.autoOnHours,
      timerConfig.autoOnMinutes,
      timerConfig.autoOnSeconds
    );
  }

  return 0;
};

/**
 * Formatea el tiempo restante en formato legible
 * Ejemplos:
 * - 3661 seg → "1 h 1 min 1 seg"
 * - 3600 seg → "1 h"
 * - 180 seg → "3 min"
 * - 90 seg → "1 min 30 seg"
 * - 45 seg → "45 seg"
 */
export const formatTimeRemaining = (seconds: number): string => {
  if (seconds <= 0) return '0 seg';
  
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);
  
  const parts: string[] = [];
  
  if (hours > 0) {
    parts.push(`${hours} h`);
  }
  
  if (minutes > 0) {
    parts.push(`${minutes} min`);
  }
  
  if (secs > 0) {
    parts.push(`${secs} seg`);
  }
  
  // Si no hay partes (caso edge), mostrar 0 seg
  if (parts.length === 0) {
    return '0 seg';
  }
  
  return parts.join(' ');
};

/**
 * Calcula el progreso del timer (0 a 1)
 */
export const calculateTimerProgress = (
  totalSeconds: number, 
  elapsedSeconds: number
): number => {
  if (totalSeconds <= 0) return 0;
  return Math.min(elapsedSeconds / totalSeconds, 1);
};

/**
 * Calcula los grados para la animación del anillo (0 a 360)
 */
export const calculateRingDegrees = (progress: number): number => {
  return Math.min(progress * 360, 360);
};

/**
 * Verifica si un timer está activo
 */
export const isTimerActive = (timerConfig: TimerConfig): boolean => {
  return timerConfig.enabled && (
    timerConfig.autoOnEnabled || 
    timerConfig.autoOffEnabled
  );
};

/**
 * Obtiene el tipo de timer activo (preferencia legacy: autoOff si ambos)
 */
export const getActiveTimerType = (timerConfig: TimerConfig): 'autoOn' | 'autoOff' | null => {
  if (!timerConfig.enabled) return null;
  
  if (timerConfig.autoOffEnabled) return 'autoOff';
  if (timerConfig.autoOnEnabled) return 'autoOn';
  
  return null;
};

/**
 * Calcula el tiempo transcurrido desde el inicio del timer
 */
export const calculateElapsedTime = (startTime: number): number => {
  return (Date.now() - startTime) / 1000;
};

const pad2 = (n: number): string => String(n ?? 0).padStart(2, '0');

export const formatTimerHms = (
  hours: number,
  minutes: number,
  seconds: number
): string => `${pad2(hours)}:${pad2(minutes)}:${pad2(seconds)}`;

export interface TimerBadgeTooltipLabels {
  title: string;
  titleOff: string;
  autoOn: string;
  autoOff: string;
  oneTime: string;
  disabled: string;
}

/** Texto multilínea para tooltip del badge Timer en la tarjeta. */
export const formatTimerBadgeTooltip = (
  timerConfig: TimerConfig | null | undefined,
  labels: TimerBadgeTooltipLabels
): string => {
  if (!timerConfig) return labels.title;

  const lines: string[] = [
    timerConfig.enabled ? labels.title : labels.titleOff,
  ];

  if (timerConfig.autoOnEnabled) {
    lines.push(
      `${labels.autoOn}: ${formatTimerHms(
        timerConfig.autoOnHours,
        timerConfig.autoOnMinutes,
        timerConfig.autoOnSeconds
      )}`
    );
  } else {
    lines.push(`${labels.autoOn}: ${labels.disabled}`);
  }

  if (timerConfig.autoOffEnabled) {
    lines.push(
      `${labels.autoOff}: ${formatTimerHms(
        timerConfig.autoOffHours,
        timerConfig.autoOffMinutes,
        timerConfig.autoOffSeconds
      )}`
    );
  } else {
    lines.push(`${labels.autoOff}: ${labels.disabled}`);
  }

  if (timerConfig.oneTimeOnly) {
    lines.push(labels.oneTime);
  }

  return lines.join('\n');
};

/**
 * Valida si un timer config es válido
 */
export const isValidTimerConfig = (timerConfig: TimerConfig): boolean => {
  if (!timerConfig) return false;
  
  const hasValidTime = (hours: number, minutes: number, seconds: number) => {
    return hours >= 0 && hours <= 23 && 
           minutes >= 0 && minutes <= 59 && 
           seconds >= 0 && seconds <= 59;
  };
  
  const autoOnValid = !timerConfig.autoOnEnabled || 
    hasValidTime(timerConfig.autoOnHours, timerConfig.autoOnMinutes, timerConfig.autoOnSeconds);
    
  const autoOffValid = !timerConfig.autoOffEnabled || 
    hasValidTime(timerConfig.autoOffHours, timerConfig.autoOffMinutes, timerConfig.autoOffSeconds);
  
  return autoOnValid && autoOffValid;
};
